<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ScenariosAPI from 'dashboard/api/scenarios';
import Button from 'dashboard/components-next/button/Button.vue';
import FieldValue from './FieldValue.vue';
import { conditionField, conditionMessages } from './conditions';
import { simulationExample, simulationFields } from './simulation';
import en from './en.json';
import ru from './ru.json';

const props = defineProps({
  scenarioId: { type: Number, required: true },
  payload: { type: Object, required: true },
  catalog: { type: Object, required: true },
  templates: { type: Array, required: true },
  visible: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);
const { t, te, locale } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...conditionMessages.en },
    ru: { ...ru, ...conditionMessages.ru },
  },
});
const example = ref(false);
const sources = ref([]);
const assignments = ref({});
const data = ref({ appointment: {}, event: {}, previous: {}, patient: {} });
const productRows = ref([]);
const consent = ref('allowed');
const now = ref(new Date().toISOString());
const eventAt = ref(now.value);
const timezone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone);
const addChannel = ref('max');
const busy = ref(false);
const error = ref('');
const result = ref(null);
const resultInput = ref('');
const resultGraph = ref({ nodes: {} });
const heading = ref(null);
const resultPanel = ref(null);
let serial = 0;
let requestId = 0;
const definition = computed(() =>
  example.value ? simulationExample(props.payload.trigger) : props.payload
);
const testNodes = computed(() =>
  Object.entries(definition.value.graph.nodes || {}).filter(([, node]) =>
    ['availability', 'message'].includes(node.type)
  )
);
const fields = computed(() => simulationFields(definition.value.graph));
const scalarFields = computed(() =>
  fields.value.filter(item => item.source !== 'appointment_service')
);
const productFields = computed(() =>
  fields.value.filter(item => item.source === 'appointment_service')
);
const fixtureNamespace = source =>
  Object.values(definition.value.graph.nodes || {})
    .flatMap(node => node.definition?.rules || [])
    .find(rule => rule.source === source && rule.reference_mis_number != null)
    ?.reference_mis_number;
const sourceFor = id => sources.value.find(source => source.id === id);
const nodeLabel = (id, node = definition.value.graph.nodes[id]) =>
  `${node?.label || node?.name || (te(node?.type || '') ? t(node.type) : id)} · ${id}`;
const channelLabel = channel =>
  te(`channel_${channel}`) ? t(`channel_${channel}`) : channel;
const sourceLabel = source =>
  `${source.name} · ${channelLabel(source.channel)}`;
const dateLabel = value =>
  value
    ? new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'short',
        timeStyle: 'medium',
      }).format(new Date(value))
    : '—';
const fieldFor = item => conditionField(props.catalog, item);
const sourceGroup = source => t(source === 'event' ? 'eventSource' : source);
const addSource = (channel = addChannel.value) => {
  if (sources.value.length >= 30) return '';
  serial += 1;
  const source = {
    id: `sim-source-${serial}`,
    name: t('simulation.sourceName', {
      channel: channelLabel(channel),
      number: serial,
    }),
    channel,
    availability: 'available',
    outcome: 'accepted',
  };
  sources.value.push(source);
  return source.id;
};
const removeSource = id => {
  sources.value = sources.value.filter(source => source.id !== id);
  Object.values(assignments.value).forEach(assignment => {
    if (assignment.source_id === id) assignment.source_id = '';
  });
};
const synchronize = () => {
  const ids = new Set(testNodes.value.map(([id]) => id));
  Object.keys(assignments.value).forEach(id => {
    if (!ids.has(id)) delete assignments.value[id];
  });
  testNodes.value.forEach(([id, node]) => {
    if (!node.channel || !props.catalog.channels.includes(node.channel)) return;
    if (!assignments.value[id]) {
      const same = testNodes.value.find(
        ([other, item]) =>
          other !== id &&
          item.channel === node.channel &&
          item.inbox_id === node.inbox_id &&
          assignments.value[other]
      );
      const unused = sources.value.find(
        source =>
          source.channel === node.channel &&
          !Object.values(assignments.value).some(
            item => item.source_id === source.id
          )
      );
      const sourceId = same
        ? assignments.value[same[0]].source_id
        : unused?.id || addSource(node.channel);
      const template = props.templates.find(
        item => item.id === node.template_id
      );
      const body =
        template?.channel_variants?.[node.channel]?.body ||
        (template?.channel === node.channel && template.body);
      assignments.value[id] = {
        source_id: sourceId,
        test_template: !body,
        text: t('simulation.defaultText'),
      };
    }
  });
};
['max', 'telegram', 'sms']
  .filter(channel => props.catalog.channels.includes(channel))
  .forEach(channel => addSource(channel));
watch([definition, () => props.catalog.channels], synchronize, {
  immediate: true,
  deep: true,
});
watch(
  () => props.visible,
  async visible => {
    if (visible) {
      await nextTick();
      heading.value?.focus();
    }
  },
  { immediate: true }
);

const settings = computed(() => ({
  sources: sources.value,
  assignments: assignments.value,
  consent: consent.value,
  timezone: timezone.value,
  now: now.value,
  event_at: eventAt.value,
  appointment_services: productRows.value.map(row => ({
    mis_number: fixtureNamespace('appointment_service') ?? 1,
    ...row,
  })),
  ...Object.fromEntries(
    ['appointment', 'event', 'previous', 'patient'].map(source => [
      source,
      {
        ...Object.fromEntries(
          scalarFields.value
            .filter(item => item.source === source)
            .map(item => [item.field, data.value[source][item.field] ?? null])
        ),
        mis_number: fixtureNamespace(source) ?? 1,
      },
    ])
  ),
}));
const requestPayload = computed(() => ({
  ...definition.value,
  settings: settings.value,
}));
const fingerprint = computed(() => JSON.stringify(requestPayload.value));
const stale = computed(
  () => result.value && resultInput.value !== fingerprint.value
);
const fallbackExample = async () => {
  example.value = true;
  await nextTick();
  sources.value.forEach(source => {
    source.availability =
      source.channel === 'sms' ? 'available' : 'unavailable';
    source.outcome = 'accepted';
  });
};
const run = async () => {
  busy.value = true;
  error.value = '';
  result.value = null;
  const input = fingerprint.value;
  requestId += 1;
  const request = requestId;
  try {
    const response = await ScenariosAPI.simulate(
      props.scenarioId,
      JSON.parse(input)
    );
    if (request === requestId) result.value = response.data;
  } catch (failure) {
    if (request !== requestId) return;
    if (failure.response?.data?.simulation)
      result.value = failure.response.data;
    else error.value = t('simulation.requestFailed');
  } finally {
    if (request === requestId) {
      resultInput.value = input;
      resultGraph.value = JSON.parse(input).graph;
      busy.value = false;
      await nextTick();
      if (props.visible) resultPanel.value?.focus();
    }
  }
};
const resultLabel = step => {
  if (typeof step.result === 'boolean')
    return t(step.result ? 'runs.conditionTrue' : 'runs.conditionFalse');
  return t(`simulation.results.${step.result}`);
};
onBeforeUnmount(() => {
  requestId += 1;
});
</script>

<template>
  <section
    class="flex flex-col w-full h-full min-h-0 min-w-0 bg-n-background"
    :aria-label="t('simulation.title')"
  >
    <header
      class="shrink-0 flex flex-wrap items-center gap-3 px-5 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <Button
        :label="t('runs.back')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        @click="emit('close')"
      />
      <h2
        ref="heading"
        tabindex="-1"
        class="m-0 text-base font-semibold flex-1"
      >
        {{ t('simulation.title') }}
      </h2>
      <Button
        :label="t('simulation.run')"
        icon="i-lucide-play"
        :disabled="busy"
        @click="run"
      />
    </header>
    <p
      class="shrink-0 m-0 px-5 py-3 text-xs text-n-amber-11 border-b border-n-weak"
    >
      {{ t('simulation.safety') }}
    </p>
    <div
      class="flex flex-1 min-h-0 min-w-0 max-lg:flex-col max-lg:overflow-y-auto"
    >
      <div
        class="lg:w-1/2 lg:overflow-y-auto p-5 min-w-0 lg:border-e border-n-weak space-y-5"
      >
        <section>
          <label class="block text-xs"
            >{{ t('simulation.graph') }}
            <select v-model="example" class="w-full mt-1 text-sm">
              <option :value="false">{{ t('simulation.currentGraph') }}</option>
              <option :value="true">{{ t('simulation.exampleGraph') }}</option>
            </select>
          </label>
          <p class="m-0 text-xs text-n-slate-11">
            {{
              example
                ? t('simulation.exampleHint')
                : t('simulation.currentHint')
            }}
          </p>
          <Button
            :label="t('simulation.fallbackExample')"
            class="mt-2"
            variant="ghost"
            size="sm"
            @click="fallbackExample"
          />
        </section>
        <section class="pt-4 border-t border-n-weak">
          <h3 class="text-sm font-semibold">{{ t('simulation.sources') }}</h3>
          <p class="text-xs text-n-slate-11">
            {{ t('simulation.sourcesHint') }}
          </p>
          <div
            v-for="source in sources"
            :key="source.id"
            class="py-3 border-b border-n-weak"
          >
            <div class="flex items-center gap-2">
              <label class="flex-1 text-xs"
                >{{ channelLabel(source.channel)
                }}<input
                  v-model="source.name"
                  maxlength="80"
                  :aria-label="t('simulation.sourceTitle')"
                  class="w-full mt-1 !mb-0 !text-sm"
              /></label>
              <Button
                :aria-label="
                  t('simulation.removeSource', { name: source.name })
                "
                icon="i-lucide-trash-2"
                variant="ghost"
                color="slate"
                @click="removeSource(source.id)"
              />
            </div>
            <div class="grid grid-cols-2 gap-3 mt-2">
              <label class="text-xs"
                >{{ t('simulation.availability')
                }}<select
                  v-model="source.availability"
                  class="w-full mt-1 !mb-0 text-sm"
                >
                  <option
                    v-for="value in ['available', 'unavailable', 'unknown']"
                    :key="value"
                    :value="value"
                  >
                    {{ t(`simulation.availability_${value}`) }}
                  </option>
                </select></label
              >
              <label class="text-xs"
                >{{ t('simulation.outcome')
                }}<select
                  v-model="source.outcome"
                  :disabled="source.availability !== 'available'"
                  class="w-full mt-1 !mb-0 text-sm"
                >
                  <option
                    v-for="value in [
                      'accepted',
                      'delivered',
                      'read',
                      'response',
                      'failed',
                      'timeout',
                      'unknown',
                    ]"
                    :key="value"
                    :value="value"
                  >
                    {{ t(`simulation.results.${value}`) }}
                  </option>
                </select></label
              >
            </div>
          </div>
          <div class="flex flex-wrap gap-2 mt-3">
            <select
              v-model="addChannel"
              :aria-label="t('channel')"
              class="!mb-0 text-sm"
            >
              <option
                v-for="channel in catalog.channels"
                :key="channel"
                :value="channel"
              >
                {{ channelLabel(channel) }}
              </option></select
            ><Button
              :label="t('simulation.addSource')"
              variant="ghost"
              :disabled="sources.length >= 30"
              @click="addSource()"
            />
          </div>
        </section>
        <section class="pt-4 border-t border-n-weak">
          <h3 class="text-sm font-semibold">
            {{ t('simulation.assignments') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('simulation.assignmentsHint') }}
          </p>
          <p v-if="!testNodes.length" class="text-xs">
            {{ t('simulation.noNodes') }}
          </p>
          <details
            v-for="[id, node] in testNodes"
            :key="id"
            class="py-3 border-b border-n-weak"
            open
          >
            <summary class="cursor-pointer text-xs font-medium">
              {{ nodeLabel(id, node) }} · {{ channelLabel(node.channel) }}
            </summary>
            <template v-if="assignments[id]">
              <label class="block text-xs mt-2"
                >{{ t('simulation.sourceTitle')
                }}<select
                  v-model="assignments[id].source_id"
                  class="w-full mt-1 text-sm"
                >
                  <option value="">{{ t('choose') }}</option>
                  <option
                    v-for="source in sources.filter(
                      item => item.channel === node.channel
                    )"
                    :key="source.id"
                    :value="source.id"
                  >
                    {{ sourceLabel(source) }}
                  </option>
                </select></label
              >
              <p
                v-if="
                  !sourceFor(assignments[id].source_id) ||
                  sourceFor(assignments[id].source_id)?.channel !== node.channel
                "
                class="text-xs text-n-ruby-11"
              >
                {{ t('simulation_source_required') }}
              </p>
              <template v-if="node.type === 'message'">
                <label class="flex items-center gap-2 text-xs"
                  ><input
                    v-model="assignments[id].test_template"
                    type="checkbox"
                    class="!m-0"
                  />{{ t('simulation.testText') }}</label
                >
                <textarea
                  v-if="assignments[id].test_template"
                  v-model="assignments[id].text"
                  maxlength="4096"
                  rows="2"
                  :aria-label="t('simulation.text')"
                  class="w-full mt-2 text-sm"
                />
                <p v-else class="mt-2 text-xs text-n-slate-11">
                  {{
                    t('simulation.templateHint', {
                      id: node.template_id || '—',
                    })
                  }}
                </p>
              </template>
            </template>
            <p v-else class="text-xs text-n-ruby-11">
              {{ t('simulation.selectChannel') }}
            </p>
          </details>
        </section>
        <section class="pt-4 border-t border-n-weak">
          <h3 class="text-sm font-semibold">{{ t('simulation.fixture') }}</h3>
          <label class="block text-xs"
            >{{ t('simulation.consent')
            }}<select v-model="consent" class="w-full mt-1 text-sm">
              <option
                v-for="value in ['allowed', 'denied', 'unknown']"
                :key="value"
                :value="value"
              >
                {{ t(`simulation.consent_${value}`) }}
              </option>
            </select></label
          >
          <details>
            <summary class="cursor-pointer text-xs font-medium">
              {{ t('simulation.fields') }}
            </summary>
            <p class="mt-2 text-xs text-n-slate-11">
              {{ t('simulation.fieldsHint') }}
            </p>
            <label
              v-for="item in scalarFields"
              :key="`${item.source}:${item.field}`"
              class="block text-xs mt-2"
              >{{ sourceGroup(item.source) }} ·
              {{ fieldFor(item).label || item.field
              }}<FieldValue
                v-model="data[item.source][item.field]"
                :field="fieldFor(item)"
                :label="`${sourceGroup(item.source)} · ${fieldFor(item).label || item.field}`"
            /></label>
            <div v-if="productFields.length" class="mt-4 space-y-3">
              <h4 class="text-sm font-medium">
                {{ t('conditionEditor.productsFixture') }}
              </h4>
              <div
                v-for="(row, index) in productRows"
                :key="index"
                class="border-t border-n-weak pt-3 space-y-2"
              >
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  color="slate"
                  :label="t('remove')"
                  @click="productRows.splice(index, 1)"
                />
                <label
                  v-for="item in productFields"
                  :key="item.field"
                  class="block text-xs"
                >
                  {{ fieldFor(item).label }}
                  <FieldValue
                    v-model="row[item.field]"
                    :field="fieldFor(item)"
                    :label="fieldFor(item).label || item.field"
                  />
                </label>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                :label="t('conditionEditor.addProductFixture')"
                @click="productRows.push({})"
              />
            </div>
            <p v-if="!fields.length" class="text-xs">
              {{ t('simulation.noFields') }}
            </p>
            <label class="block text-xs mt-2"
              >{{ t('simulation.now')
              }}<input
                v-model="now"
                :aria-label="t('simulation.now')"
                class="w-full mt-1 text-sm"
            /></label>
            <label class="block text-xs mt-2"
              >{{ t('simulation.eventAt')
              }}<input
                v-model="eventAt"
                :aria-label="t('simulation.eventAt')"
                class="w-full mt-1 text-sm"
            /></label>
            <label class="block text-xs mt-2"
              >{{ t('timezone')
              }}<select v-model="timezone" class="w-full mt-1 text-sm">
                <option
                  v-for="zone in catalog.timezones"
                  :key="zone"
                  :value="zone"
                >
                  {{ zone }}
                </option>
              </select></label
            >
          </details>
        </section>
      </div>
      <section
        ref="resultPanel"
        tabindex="-1"
        class="lg:w-1/2 min-w-0 lg:overflow-y-auto p-5"
        :aria-label="t('simulation.result')"
        :aria-busy="busy"
      >
        <h3 class="text-sm font-semibold">{{ t('simulation.result') }}</h3>
        <p v-if="busy" role="status" class="text-sm">
          {{ t('simulation.running') }}
        </p>
        <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
          {{ error }}
        </p>
        <p v-if="!result && !busy && !error" class="text-sm text-n-slate-11">
          {{ t('simulation.empty') }}
        </p>
        <template v-if="result">
          <p class="text-sm font-medium">
            {{
              t('simulation.zeroSends', {
                count: result.simulated_attempts || 0,
              })
            }}
          </p>
          <p v-if="stale" role="status" class="text-xs text-n-amber-11">
            {{ t('simulation.stale') }}
          </p>
          <p
            v-for="(issue, index) in result.errors"
            :key="index"
            role="alert"
            class="text-sm text-n-ruby-11"
          >
            {{ issue.node }} ·
            {{ te(issue.code) ? t(issue.code) : t('simulation.invalid') }}
          </p>
          <ol class="m-0 p-0 list-none divide-y divide-n-weak">
            <li v-for="(step, index) in result.trace" :key="index" class="py-3">
              <p class="m-0 text-sm font-medium">
                {{ index + 1 }}.
                {{ nodeLabel(step.node, resultGraph.nodes[step.node]) }}
              </p>
              <p
                v-if="step.source_name"
                class="m-0 mt-1 text-xs text-n-slate-11"
              >
                {{ step.source_name }} · {{ channelLabel(step.channel) }} ·
                {{ t('simulation.virtual') }}
              </p>
              <p class="m-0 mt-1 text-sm">{{ resultLabel(step) }}</p>
              <p v-if="step.edge" class="m-0 mt-1 text-xs">
                {{ t('runs.branch', { branch: t(step.edge) }) }}
              </p>
              <p
                v-if="
                  step.duplicate_risk ||
                  (step.attempt && step.result === 'unknown')
                "
                class="mt-1 text-xs text-n-amber-11"
              >
                {{ t('simulation.duplicateRisk') }}
              </p>
              <p class="m-0 mt-1 text-xs text-n-slate-11">
                {{ dateLabel(step.at)
                }}<span v-if="step.until">
                  · {{ t('runtime.waitUntil') }}
                  {{ dateLabel(step.until) }}</span
                >
              </p>
              <details v-if="step.text" class="mt-2 text-xs">
                <summary class="cursor-pointer">
                  {{
                    step.test_template
                      ? t('simulation.text')
                      : t('simulation.rawTemplate')
                  }}
                </summary>
                <p class="mt-2 text-sm whitespace-pre-wrap">{{ step.text }}</p>
                <p v-if="step.buttons?.length" class="text-xs">
                  {{ t('runs.buttons') }}: {{ step.buttons.join(' · ') }}
                </p>
              </details>
            </li>
          </ol>
          <p class="mt-4 text-xs text-n-slate-11">
            {{ t('simulation.resultHint') }}
          </p>
        </template>
      </section>
    </div>
  </section>
</template>
